import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return url.startsWith("http://") || url.startsWith("https://");
  } catch {
    return false;
  }
};

// Parameter definitions
const parameterDefinitions = [
  { key: "description", label: "Description" },
  { key: "category", label: "Category" },
  { key: "manufacturingLine", label: "Manufacturing Line" },
  { key: "rootCause", label: "Root Cause" },
  { key: "correctiveAction", label: "Corrective Action" },
  { key: "preventiveAction", label: "Preventive Action" },
  { key: "initiationDate", label: "Initiation Date" },
  { key: "targetDate", label: "Target Date" },
  { key: "closingDate", label: "Closing Date" },
  { key: "delayedDays", label: "Delayed Days" },
  { key: "status", label: "Status" },
  { key: "zip_url", label: "Zip URL" },
];

const defaultProblem = {
  description: "N/A",
  category: "N/A",
  manufacturingLine: "N/A",
  rootCause: "N/A",
  correctiveAction: "N/A",
  preventiveAction: "N/A",
  initiationDate: "N/A",
  targetDate: "N/A",
  closingDate: "N/A",
  delayedDays: "N/A",
  status: "N/A",
  inspectorEvidenceCount: 0,
  vendorEvidenceCount: 0,
  zip_url: "N/A",
};

const RootCauseAnalysisTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Validate and filter data
  const validData = useMemo(() => {
    return (data || []).filter((item) => {
      return item && item.inspectionDate && item.inspectionShift && item.form_data;
    });
  }, [data]);

  // Static headers
  const staticHeaders = [{ id: "parameter", label: "Parameter" }];

  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(validData.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, validData]);

  // Build lookup map for problems by date and shift
  const dataMap = useMemo(() => {
    const map = {};
    validData.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;

      const key = `${date}_${shift}`;
      const problems = Array.isArray(item.form_data?.problems) ? item.form_data.problems : [];
      map[key] = problems.map((problem) => ({
        ...defaultProblem,
        ...problem,
        zip_url:
          item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url)
            ? item.zip_url.trim()
            : "N/A",
      }));

      if (map[key].length === 0) {
        map[key] = [
          {
            ...defaultProblem,
            zip_url:
              item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url)
                ? item.zip_url.trim()
                : "N/A",
          },
        ];
      }
    });
    return map;
  }, [validData]);

  // Determine number of problem columns per date-shift (minimum 1)
  const maxProblemsPerShift = useMemo(() => {
    const maxCounts = {};
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const count = (dataMap[key] || []).length;
        maxCounts[key] = Math.max(count, 1);
      });
    });
    return maxCounts;
  }, [dates, effectiveShifts, dataMap]);

  // Generate hierarchical header tree: Date -> Shifts -> Problems
  const headerStructure = useMemo(() => {
    return dates.map((date) => {
      let dateColSpan = 0;
      const shiftNodes = effectiveShifts.map((shift) => {
        const key = `${date}_${shift}`;
        const count = maxProblemsPerShift[key] || 1;
        dateColSpan += count;

        const problems = Array.from({ length: count }, (_, i) => ({
          id: `${key}_problem_${i + 1}`,
          label: `Problem ${i + 1}`,
          date,
          shift,
          problemIndex: i,
        }));

        return {
          shift,
          colSpan: count,
          problems,
        };
      });

      return {
        date,
        colSpan: dateColSpan,
        shifts: shiftNodes,
      };
    });
  }, [dates, effectiveShifts, maxProblemsPerShift]);

  // Flattened leaf columns for the table body
  const flatColumns = useMemo(() => {
    return headerStructure.flatMap((d) => d.shifts.flatMap((s) => s.problems));
  }, [headerStructure]);

  // Column width calculations
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: 200,
      value: 150,
    };

    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.max(widths.parameter, estimateTextWidth(param.label));
    });

    return widths;
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Static parameter rows
  const tableRows = useMemo(() => {
    return parameterDefinitions.map((param) => ({
      key: param.key,
      parameter: param.label,
    }));
  }, []);

  // Lookup map: `${date}_${shift}_${problemIndex}_${paramKey}`
  const tableDataMap = useMemo(() => {
    const map = {};
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const problems = dataMap[key] || [];
        const count = maxProblemsPerShift[key] || 1;

        for (let i = 0; i < count; i++) {
          const prob = problems[i] || defaultProblem;
          parameterDefinitions.forEach((param) => {
            const val = prob[param.key];
            map[`${key}_${i}_${param.key}`] = val !== undefined && val !== "" ? String(val) : "N/A";
          });
        }
      });
    });
    return map;
  }, [dates, effectiveShifts, dataMap, maxProblemsPerShift]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Parameter"];
    const row2 = [""];
    const row3 = [""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 2, c: 0 } });

    let colIdx = 1;

    headerStructure.forEach((dateNode) => {
      row1.push(dateNode.date);
      for (let i = 1; i < dateNode.colSpan; i++) row1.push("");
      if (dateNode.colSpan > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + dateNode.colSpan - 1 } });
      }

      dateNode.shifts.forEach((shiftNode) => {
        row2.push(`Shift ${shiftNode.shift}`);
        for (let i = 1; i < shiftNode.colSpan; i++) row2.push("");
        if (shiftNode.colSpan > 1) {
          merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + shiftNode.colSpan - 1 } });
        }

        shiftNode.problems.forEach((prob) => {
          row3.push(prob.label);
          colIdx += 1;
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      flatColumns.forEach((col) => {
        const val = tableDataMap[`${col.date}_${col.shift}_${col.problemIndex}_${row.key}`] || "N/A";
        if (row.key === "zip_url" && val !== "N/A" && val.startsWith("http")) {
          r.push("LINK");
        } else {
          r.push(val);
        }
      });
      return r;
    });

    const sheetData = [row1, row2, row3, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    tableRows.forEach((row, rowIndex) => {
      if (row.key === "zip_url") {
        flatColumns.forEach((col, colIndex) => {
          const rawUrl = tableDataMap[`${col.date}_${col.shift}_${col.problemIndex}_${row.key}`];
          if (
            rawUrl &&
            rawUrl !== "N/A" &&
            typeof rawUrl === "string" &&
            rawUrl.trim().toLowerCase().startsWith("http")
          ) {
            const cleanUrl = rawUrl.trim();
            const cellAddress = XLSX.utils.encode_cell({
              r: rowIndex + 3,
              c: colIndex + 1,
            });

            ws[cellAddress] = {
              t: "s",
              v: "LINK",
              l: { Target: cleanUrl, Tooltip: cleanUrl },
            };
          }
        });
      }
    });

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 25 },
      ...Array(flatColumns.length).fill({ wch: 20 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Root Cause Analysis");

    const fileName = generateExcelFileName("Root_Cause_Analysis", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", bookSST: false });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Root Cause Analysis
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Parameter + Dates (merged across shifts and their problems) */}
            <TableRow>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {headerStructure.map((dateNode) => (
                <CustomTableCell
                  key={dateNode.date}
                  colSpan={dateNode.colSpan}
                  columnWidth={columnWidths.value * dateNode.colSpan}
                >
                  {dateNode.date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shifts (sub-columns under Date) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.map((shiftNode) => (
                  <CustomTableCell
                    key={`${dateNode.date}_${shiftNode.shift}`}
                    colSpan={shiftNode.colSpan}
                    columnWidth={columnWidths.value * shiftNode.colSpan}
                  >
                    Shift {shiftNode.shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Problems (sub-columns under Shift) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.flatMap((shiftNode) =>
                  shiftNode.problems.map((prob) => (
                    <CustomTableCell
                      key={prob.id}
                      columnWidth={columnWidths.value}
                    >
                      {prob.label}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={1}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>

                  {flatColumns.map((col) => {
                    const value =
                      tableDataMap[`${col.date}_${col.shift}_${col.problemIndex}_${row.key}`] || "N/A";
                    return (
                      <BodyTableCell key={`${col.id}_${index}`} columnWidth={columnWidths.value}>
                        {row.key === "zip_url" && value !== "N/A" && value.startsWith("http") ? (
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", textDecoration: "underline" }}
                          >
                            LINK
                          </a>
                        ) : (
                          value
                        )}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RootCauseAnalysisTable;